import { useState, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { MonitorPlay, Sparkles, Users, BarChart3, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { Card, CardContent } from "@/components/ui/card";
import { AuthHeader } from "@/components/auth/AuthHeader";
import { AuthForm } from "@/components/auth/AuthForm";
import { VerificationForm } from "@/components/auth/VerificationForm";
import { PasswordResetForm } from "@/components/auth/form/PasswordResetForm";
import { SignupDisabledAlert } from "@/components/auth/SignupDisabledAlert";
import { SocialAuthButtons } from "@/components/auth/SocialAuthButtons";
import { useAuthFlow } from "@/hooks/useAuthFlow";
import logoUrl from "@/assets/fusion-logo-new.png";

const Auth = () => {
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [animateCard, setAnimateCard] = useState(false);
  const [isPasswordReset, setIsPasswordReset] = useState(false);

  const {
    isSignUp,
    isLoading,
    isVerifying,
    pendingVerificationEmail,
    signupDisabled,
    handleSubmit,
    handleVerifyCode,
    handleResendCode,
    handleBackToLogin,
    handleToggleMode,
    from,
  } = useAuthFlow();

  useEffect(() => {
    const urlHash = window.location.hash;
    if (urlHash.includes("access_token") && urlHash.includes("type=recovery")) {
      setIsPasswordReset(true);
      return;
    }
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("verification") === "true") {
      const storedEmail = localStorage.getItem("pendingVerificationEmail");
      if (storedEmail) {
        handleVerifyCode(storedEmail);
      }
    }
  }, []);

  useEffect(() => {
    setAnimateCard(true);
    const timer = setTimeout(() => setAnimateCard(false), 300);
    return () => clearTimeout(timer);
  }, [isSignUp, isVerifying]);

  const handlePasswordResetComplete = () => {
    setIsPasswordReset(false);
    window.history.replaceState({}, document.title, window.location.pathname);
    navigate("/", { replace: true });
  };

  if (user && !isPasswordReset) {
    return <Navigate to={from} replace />;
  }

  const isAr = language === "ar";

  const features = [
    {
      icon: MonitorPlay,
      label: isAr ? "شبكة DOOH فورية في كامل المملكة" : "Real-time DOOH network across KSA",
    },
    {
      icon: Sparkles,
      label: isAr ? "مخطط الحملات بالذكاء الاصطناعي" : "AI-powered campaign planner",
    },
    {
      icon: Users,
      label: isAr ? "وصول مباشر إلى أبرز المؤثرين" : "Direct access to top influencers",
    },
    {
      icon: BarChart3,
      label: isAr ? "تحليلات أداء حية للحملات" : "Live campaign performance analytics",
    },
  ];

  return (
    <div className="relative min-h-screen lg:h-screen lg:overflow-hidden w-full grid lg:grid-cols-2 bg-background">

      {/* Brand panel (lg+) */}
      <div className="relative hidden lg:block lg:h-screen px-10 xl:px-12 overflow-hidden">
        <div className="absolute inset-0 bg-conic-drift pointer-events-none" aria-hidden="true" />
        <div className="absolute inset-0 bg-grid-fade pointer-events-none" aria-hidden="true" />
        <div
          className="absolute -top-32 -left-24 w-[28rem] h-[28rem] animate-blob"
          style={{ background: "hsl(var(--primary) / 0.30)", filter: "blur(60px)" }}
        />
        <div
          className="absolute top-1/3 -right-32 w-[32rem] h-[32rem] animate-blob-slow"
          style={{ background: "hsl(var(--primary-light) / 0.25)", filter: "blur(60px)" }}
        />
        <div
          className="absolute -bottom-32 left-1/4 w-[26rem] h-[26rem] animate-blob"
          style={{
            background: "hsl(var(--primary) / 0.22)",
            filter: "blur(60px)",
            animationDelay: "-6s",
          }}
        />

        {/* Desktop logo */}
        <div
          className="absolute -top-4 -left-4 h-[17rem] xl:h-[20rem] w-[45rem] xl:w-[50rem] animate-fade-in pointer-events-none"
          aria-hidden="true"
        >
          <img
            src={logoUrl}
            alt="Fusion Media"
            className="h-full w-full object-contain object-left-top"
          />
        </div>

        {/* Marketing block - absolute deterministic position */}
        <div className="absolute top-60 xl:top-64 left-10 xl:left-12 right-10 xl:right-12 space-y-5 animate-fade-in">
          <h1 className="text-4xl xl:text-5xl font-bold leading-tight tracking-tight">
            {isAr ? (
              <>
                شغّل إعلاناتك بكامل قوة <span className="text-shine">Fusion</span>.
              </>
            ) : (
              <>
                Power your advertising with <span className="text-shine">Fusion</span>.
              </>
            )}
          </h1>
          <p className="text-lg text-muted-foreground max-w-md">
            {isAr
              ? "أدر حملاتك الإعلانية وشاشاتك الرقمية والمؤثرين والفعاليات من غرفة تحكم أنيقة واحدة."
              : "Run campaigns, DOOH faces, influencers, and events from one elegant control room."}
          </p>
          <div className="grid grid-cols-1 gap-3 max-w-md pt-4 stagger">
            {features.map((f) => (
              <div
                key={f.label}
                className="flex items-center gap-3 glass-card rounded-xl p-3 hover-lift"
              >
                <div className="w-9 h-9 rounded-lg gradient-primary flex items-center justify-center text-primary-foreground shadow-glow">
                  <f.icon className="w-4 h-4" />
                </div>
                <span className="text-sm font-medium">{f.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="absolute bottom-6 left-10 xl:left-12 text-xs text-muted-foreground">
          © Fusion Media — {isAr ? "منصة الإعلانات الذكية" : "AI-powered advertising platform"}
        </div>
      </div>

      {/* Form panel */}
      <div className="relative flex items-center justify-center px-5 pt-20 pb-10 lg:p-8 xl:p-12 lg:overflow-y-auto">
        <div className="absolute inset-0 lg:hidden bg-conic-drift" />

        <button
          type="button"
          onClick={() => navigate("/")}
          className="absolute top-4 right-4 lg:top-6 lg:right-6 z-30 inline-flex items-center gap-2 h-10 px-4 rounded-full bg-surface/90 backdrop-blur border border-border text-foreground text-sm font-semibold hover:bg-surface-muted transition-colors shadow-card"
        >
          <ArrowLeft className={`w-4 h-4 ${isAr ? "rotate-180" : ""}`} />
          {isAr ? "العودة للرئيسية" : "Back to home"}
        </button>

        <div className="relative w-full max-w-md">
          {/* Mobile-only logo, in flow so it never overlaps the form */}
          <img
            src={logoUrl}
            alt="Fusion Media"
            className="lg:hidden w-48 h-auto object-contain mx-auto mb-2 animate-fade-in"
          />

          {!isPasswordReset && (
            <div className="mb-6">
              <AuthHeader isSignUp={isSignUp} isVerifying={isVerifying} />
            </div>
          )}


          <Card
            className={`relative overflow-hidden glass-card shadow-elegant transition-all duration-300 ${
              animateCard ? "scale-[0.98] opacity-90" : "scale-100 opacity-100"
            }`}
          >
            <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-primary via-primary-light to-primary" />
            <CardContent className="p-6 sm:p-8 pt-8">
              {isPasswordReset ? (
                <PasswordResetForm onComplete={handlePasswordResetComplete} />
              ) : (
                <>
                  {signupDisabled && <SignupDisabledAlert onBackToLogin={handleBackToLogin} />}

                  {isVerifying ? (
                    <VerificationForm
                      pendingVerificationEmail={pendingVerificationEmail}
                      isLoading={isLoading}
                      onVerify={handleVerifyCode}
                      onResendCode={handleResendCode}
                      onBackToLogin={handleBackToLogin}
                    />
                  ) : (
                    !signupDisabled && (
                      <>
                        <AuthForm
                          isSignUp={isSignUp}
                          isLoading={isLoading}
                          onSubmit={handleSubmit}
                          onToggleMode={handleToggleMode}
                        />
                        <SocialAuthButtons />
                      </>
                    )
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Auth;
